import { AttitudeState, WorkflowSubmission } from '@aero-platform/shared'

export type ReferenceRunEntry = { name: string; cases: string[] }

/**
 * Returns a unique id string. Prefers crypto.randomUUID() but falls back to a
 * timestamp + random suffix where it is unavailable (older browsers, some test
 * environments) so callers never throw "crypto.randomUUID is not a function".
 */
export const generateUniqueId = (): string => {
  const c = globalThis.crypto
  if (c && typeof c.randomUUID === 'function') return c.randomUUID()
  return `${Date.now()}-${Math.random().toString(36).slice(2, 10)}`
}

/**
 * Transforms an array of WorkflowSubmission objects (with limited projection) into
 * grouped { name, cases } reference run entries. Deduplicates workflowRunGroups by
 * attitude state, keeping the entry with the highest trailing numeric systemRunId.
 */
export const transformSubmissionsToReferenceRuns = (
  submissions: WorkflowSubmission[],
  sourceSubmission: WorkflowSubmission,
  caseName: string
): ReferenceRunEntry[] => {
  const targetAttitudeState = sourceSubmission.submissionRunGroups?.reduce<
    AttitudeState | undefined
  >((acc, srg) => {
    if (acc) return acc
    const wrg = srg.workflowRunGroups?.find(wrg => wrg.systemRunId.includes(caseName))
    if (wrg) {
      return wrg.runGroupCase?.selectedAttitudeState
    }
    return undefined
  }, undefined)

  return submissions
    .map(submission => {
      const latestByAttitude = new Map<string, { systemRunId: string; numericId: number }>()

      for (const srg of submission.submissionRunGroups ?? []) {
        for (const wrg of srg.workflowRunGroups ?? []) {
          const attState = wrg.runGroupCase?.selectedAttitudeState
          const sysRunId = wrg.systemRunId
          if (!attState || !sysRunId) continue

          const attKey = [
            attState.roll ?? 0,
            attState.yaw ?? 0,
            attState.frh ?? 0,
            attState.rrh ?? 0,
            attState.steer ?? 0
          ].join('_')

          const numericMatch = sysRunId.match(/(\d+)$/)
          const numericId = numericMatch ? parseInt(numericMatch[1], 10) : 0

          const existing = latestByAttitude.get(attKey)
          if (!existing || numericId > existing.numericId) {
            if (targetAttitudeState) {
              const rollDiff = Math.abs(attState.roll - targetAttitudeState.roll)
              const yawDiff = Math.abs(attState.yaw - targetAttitudeState.yaw)
              const pitchDiff = Math.abs(attState.frh - targetAttitudeState.frh)
              const rrhDiff = Math.abs(attState.rrh - targetAttitudeState.rrh)
              const steerDiff = Math.abs(attState.steer - targetAttitudeState.steer)
              const tolerance = 0.0001
              if (
                rollDiff > tolerance ||
                yawDiff > tolerance ||
                pitchDiff > tolerance ||
                rrhDiff > tolerance ||
                steerDiff > tolerance
              ) {
                continue // Skip this entry if it doesn't match the target attitude state within tolerance
              }
            }
            latestByAttitude.set(attKey, { systemRunId: sysRunId, numericId })
          }
        }
      }

      return {
        name: submission.name,
        cases: Array.from(latestByAttitude.values()).map(v => v.systemRunId)
      }
    })
    .filter(r => r.cases.length > 0)
}

/**
 * Parses an array of reference run strings (format: "<RunName>|<CaseName>") into
 * grouped { name, cases } entries, accumulating multiple cases per run name.
 */
export const parseReferenceRunStrings = (referenceRunStrings: string[]): ReferenceRunEntry[] => {
  const runMap = new Map<string, string[]>()
  for (const entry of referenceRunStrings ?? []) {
    const pipeIndex = entry.indexOf('|')
    if (pipeIndex === -1) continue
    const runName = entry.slice(0, pipeIndex)
    const caseName = entry.slice(pipeIndex + 1)
    if (!runName || !caseName) continue
    const existing = runMap.get(runName) ?? []
    existing.push(caseName)
    runMap.set(runName, existing)
  }
  return Array.from(runMap.entries()).map(([name, cases]) => ({ name, cases }))
}

export const getMatchingCaseByCarState = (
  srcCaseAttitudeState: AttitudeState,
  referenceCase: WorkflowSubmission
): string => {
  for (const srg of referenceCase.submissionRunGroups ?? []) {
    for (const wrg of srg.workflowRunGroups ?? []) {
      const selectedAttitudeState = wrg.runGroupCase?.selectedAttitudeState
      // Check if the selectedAttitudeState matches the srcCaseAttitudeState within a certail tolerance
      if (selectedAttitudeState) {
        const rollDiff = Math.abs(selectedAttitudeState?.roll - srcCaseAttitudeState?.roll)
        const yawDiff = Math.abs(selectedAttitudeState?.yaw - srcCaseAttitudeState?.yaw)
        const steerDiff = Math.abs(selectedAttitudeState?.steer - srcCaseAttitudeState?.steer)
        const frhDiff = Math.abs(selectedAttitudeState?.frh - srcCaseAttitudeState?.frh)
        const rrhDiff = Math.abs(selectedAttitudeState?.rrh - srcCaseAttitudeState?.rrh)

        const tolerance = 0.0001 // Define a tolerance value for comparison
        if (
          rollDiff <= tolerance &&
          yawDiff <= tolerance &&
          steerDiff <= tolerance &&
          frhDiff <= tolerance &&
          rrhDiff <= tolerance
        ) {
          return wrg.systemRunId // Return the systemRunId if the selectedAttitudeState matches the srcCaseAttitudeState
        }
      }
    }
  }

  return ''
}
